import { motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { AriaCardGrid } from "@/components/aria/AriaCards";
import { AriaMark } from "@/components/AriaMark";
import { Send, Loader2, Play, Bookmark, X, Check, Workflow, MousePointerClick } from "lucide-react";

const Turn = ({ turn }) => {
  if (turn.role === "user") {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-row-reverse gap-2.5" data-testid="aria-turn-user">
        <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#222] text-[9px] font-semibold text-[#c9c9c9]">AT</div>
        <div className="max-w-[80%] rounded-2xl bg-[#4F8EF7] px-3.5 py-2.5 text-[13px] text-white">{turn.text}</div>
      </motion.div>
    );
  }
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2.5" data-testid="aria-turn-aria">
      <AriaMark size={26} className="mt-1" />
      <div className="min-w-0 flex-1">
        {turn.loading ? (
          <div className="inline-flex items-center gap-2 rounded-2xl bg-[#161616] px-3.5 py-2.5 text-[12px] text-[#A3A3A3]">
            <Loader2 size={13} className="animate-spin text-[#4F8EF7]" /> Aria is working through your request…
          </div>
        ) : (
          <>
            <div className="inline-block rounded-2xl bg-[#161616] px-3.5 py-2.5 text-[13px] leading-relaxed text-[#e5e5e5]">{turn.text}</div>
            <AriaCardGrid cards={turn.cards} />
          </>
        )}
      </div>
    </motion.div>
  );
};

const WorkflowsRow = ({ chat }) => (
  <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="aria-workflows">
    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]"><Workflow size={11} /> Workflows</span>
    {chat.workflows.map((wf) => (
      <div key={wf.id} className="group flex items-center">
        <button onClick={() => chat.runWorkflow(wf)} disabled={chat.running} data-testid={`aria-workflow-run-${wf.id}`}
          className="flex items-center gap-1.5 rounded-md bg-[rgba(79,142,247,0.12)] px-2.5 py-1 text-[11px] text-[#cfe0fb] transition-colors hover:bg-[rgba(79,142,247,0.2)] disabled:opacity-40">
          <Play size={10} className="text-[#7FAEF9]" /> {wf.name}
          <span className="text-[9.5px] text-[#6b6b6b]">· {wf.steps.length}</span>
        </button>
        <button onClick={(e) => chat.deleteWorkflow(wf.id, e)} title="Delete workflow" data-testid={`aria-workflow-delete-${wf.id}`}
          className="ml-0.5 hidden h-5 w-5 items-center justify-center rounded-md text-[#6b6b6b] hover:text-[#F87171] group-hover:flex">
          <X size={11} />
        </button>
      </div>
    ))}
    {!chat.saving && chat.sessionCommands.length > 0 && (
      <button onClick={() => chat.setSaving(true)} data-testid="aria-save-workflow"
        className="flex items-center gap-1 rounded-md border border-dashed border-[#2a2a2a] px-2.5 py-1 text-[11px] text-[#A3A3A3] transition-colors hover:text-white">
        <Bookmark size={10} /> Save as workflow
      </button>
    )}
    {chat.saving && (
      <div className="flex items-center gap-1.5 rounded-md bg-[#0d0d0d] px-1.5 py-0.5">
        <Input value={chat.saveName} onChange={(e) => chat.setSaveName(e.target.value)} autoFocus
          onKeyDown={(e) => { if (e.key === "Enter") chat.saveWorkflow(); if (e.key === "Escape") { chat.setSaving(false); chat.setSaveName(""); } }}
          placeholder={`Name (${chat.sessionCommands.length} steps)`} data-testid="aria-workflow-name-input"
          className="h-6 w-40 border-0 bg-transparent p-1 text-[11.5px] text-white placeholder:text-[#555] focus-visible:ring-0" />
        <button onClick={chat.saveWorkflow} data-testid="aria-workflow-save-confirm" className="flex h-5 w-5 items-center justify-center rounded-md btn-gradient text-white"><Check size={11} /></button>
        <button onClick={() => { chat.setSaving(false); chat.setSaveName(""); }} className="flex h-5 w-5 items-center justify-center rounded-md text-[#6b6b6b] hover:text-white"><X size={11} /></button>
      </div>
    )}
  </div>
);

const SelectionChips = ({ selection, setSelection }) => (
  <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="aria-selection-chips">
    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#7FAEF9]">
      <MousePointerClick size={11} /> {selection.length} selected
    </span>
    {selection.slice(0, 6).map((s) => (
      <span key={s.key} data-testid={`aria-selection-chip-${s.key}`}
        className="flex items-center gap-1 rounded-md bg-[rgba(79,142,247,0.12)] py-0.5 pl-2 pr-1 text-[10.5px] text-[#cfe0fb]">
        <span className="max-w-[150px] truncate">{s.label}</span>
        <button onClick={() => setSelection((sel) => sel.filter((x) => x.key !== s.key))}
          className="flex h-3.5 w-3.5 items-center justify-center rounded text-[#6b6b6b] hover:text-white">
          <X size={9} />
        </button>
      </span>
    ))}
    {selection.length > 6 && <span className="text-[10px] text-[#6b6b6b]">+{selection.length - 6} more</span>}
    <button onClick={() => setSelection([])} data-testid="aria-selection-clear"
      className="text-[10px] text-[#6b6b6b] underline-offset-2 hover:text-white hover:underline">Clear</button>
  </div>
);

export const ChatTab = ({ chat, selection, setSelection, suggestions, pageName }) => (
  <>
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="ask-aria-conversation">
      {chat.turns.map((t) => <Turn key={t.id} turn={t} />)}
      <div ref={chat.bottomRef} />
    </div>

    <div className="border-t border-[#1e1e1e] p-3">
      <WorkflowsRow chat={chat} />

      <div className="mb-2 flex flex-wrap gap-1.5" data-testid="aria-suggestions">
        {suggestions.map((p) => (
          <button key={p} onClick={() => chat.runCommand(p)} disabled={chat.running} data-testid="aria-suggestion-chip"
            className="rounded-md bg-[#1a1a1a] px-2.5 py-1 text-[11px] text-[#A3A3A3] transition-colors hover:bg-[#222] hover:text-white disabled:opacity-40">
            {p}
          </button>
        ))}
      </div>

      {selection.length > 0 && <SelectionChips selection={selection} setSelection={setSelection} />}

      <div className="flex items-end gap-2 rounded-2xl border border-[#2a2a2a] bg-[#161616] p-2 focus-within:border-[rgba(79,142,247,0.4)]">
        <Textarea value={chat.input} onChange={(e) => chat.setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); chat.runCommand(); } }}
          placeholder={selection.length
            ? `What should I do with the ${selection.length} selected object${selection.length > 1 ? "s" : ""}? e.g. approve them…`
            : `Ask Aria anything on ${pageName}…`}
          rows={1} data-testid="aria-command-input"
          className="min-h-[36px] max-h-32 flex-1 resize-none border-0 bg-transparent p-1.5 text-[13px] text-white placeholder:text-[#555] focus-visible:ring-0" />
        <button onClick={() => chat.runCommand()} disabled={!chat.input.trim() || chat.running} data-testid="aria-command-send"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl btn-gradient text-white transition-opacity disabled:opacity-30">
          {chat.running ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
    </div>
  </>
);
